import { PrismicRichText, SliceZone } from '@prismicio/react';
import Head from 'next/head';
import { useEffect, useRef, useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { DocumentHead } from 'remote-com-shared/components/DocumentHead';
import HeaderContext from 'remote-com-shared/components/Header/context';
import { ToggleContainer } from 'remote-com-shared/components/HeaderNavigation/styles/Toggle';
import { SectionWrapper } from 'remote-com-shared/components/SectionWrapper';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import smoothscroll from 'smoothscroll-polyfill';
import styled, { createGlobalStyle } from 'styled-components';

import { BenefitsPlanTableSlice } from './BenefitsPlanTableSlice';
import { BenefitsSlice } from './BenefitsSlice';
import { ContentSlice } from './ContentSlice';
import { CountryContentSlice } from './CountryContentSlice';
import { FlexibleSupplementalSlice } from './FlexibleSupplementalSlice';
import { HeroSlice } from './HeroSlice';
import { LinkToPricingTableSlice } from './LinkToPricingTableSlice';
import { MoreInformationSlice } from './MoreInformationSlice';
import { TableSlice } from './TableSlice';
import { TwoFourColumnsTable } from './TwoFourColumnsTable';
import { VideoEmbedSlice } from './VideoEmbedSlice';

import { BenefitsLayout } from '@/layouts/BenefitsLayout';
import { FooterContainerWrapper } from '@/src/components/Footer/WebinarFooter';

if (typeof window !== 'undefined') {
  smoothscroll.polyfill();
}

// The benefit guides have no social image set in Prismic, so they would fall back to the old-brand
// default in DocumentHead; use the rebrand share image (2400x1260) instead.
const socialImage = {
  url: staticAssetUrl('/images/remote-share-image-logo-lockup.webp'),
  alt: null,
  dimensions: { width: 2400, height: 1260 },
};

const width = 1200;
const pxTocm = 0.02571;

const PageWrapper = styled.div`
  ${ToggleContainer} {
    display: none;
  }
`;

const VideoWrapper = styled(SectionWrapper)``;

const Title = styled.div`
  ${fluidProp('font-size', '38px', '48px')}
  color: var(--newBrandColor);
  font-family: var(--typography-fontBrand);
  line-height: 113.5%;
  font-weight: 700;

  > * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    margin: 0;
  }
`;

const PrintStyle = createGlobalStyle`
  @media print {
    body {
      /* max-width: ${width}px; */
      margin-left: 0;
      margin-right: 0;
    }
    html {
      background-color: #14191e;
    }

    ${PageWrapper} {
      background-color: var(--colors-blank);
    }

    ${FooterContainerWrapper} {
      width: 85%;
      margin: auto;
    }

    ${VideoWrapper} {
      display: none;
    }
  }

  @page {
    margin: 0;
    size: ${({ pageHeight }) => `${width * pxTocm}cm ${pageHeight * pxTocm}cm`};
  }
`;

const components = {
  benefits_hero_country_: HeroSlice,
  benefits_title__description__note: ContentSlice,
  benefits_more_information: MoreInformationSlice,
  benefits_additional: BenefitsSlice,
  benefits_table_3_column: TableSlice,
  benefits_country_hiring: CountryContentSlice,
  benefits_flexible_supplemental: FlexibleSupplementalSlice,
  pricing_plan_table: BenefitsPlanTableSlice,
  table_two_three_four_column: TwoFourColumnsTable,
  link_to_pricing_table: LinkToPricingTableSlice,
  video_embed: VideoEmbedSlice,
};

export function CountryBenefitsPage(document) {
  const headerRef = useRef(null);
  const { body, sharedData } = document;
  const applyFormRef = useRef();
  const pageWrapper = useRef();
  const [pageHeight, setPageHeight] = useState(0);

  const { shared_section_title: sharedSectionTitle, body: sharedBody } = sharedData;

  useEffect(() => {
    function calculatePageHeight() {
      setPageHeight(window.document.body.clientHeight);
    }

    window.addEventListener('resize', calculatePageHeight);
    window.addEventListener('load', calculatePageHeight);
    calculatePageHeight();

    return () => {
      window.removeEventListener('resize', calculatePageHeight);
      window.removeEventListener('load', calculatePageHeight);
    };
  }, [document]);

  return (
    <PageWrapper ref={pageWrapper}>
      <PrintStyle pageHeight={pageHeight} />
      <DocumentHead
        document={{ ...document, social_image: document.social_image ?? socialImage }}
        noIndex
      />
      <HeaderContext.Provider value={headerRef}>
        <Head>
          <link rel="stylesheet" href={staticAssetUrl('/fonts/import-rotina.css')} />
        </Head>
        <SliceZone slices={body} components={components} context={{ applyFormRef }} />
        <VideoWrapper>
          <ContentContainer>
            <Title>
              <PrismicRichText field={sharedSectionTitle} />
            </Title>
          </ContentContainer>
          <SliceZone slices={sharedBody} components={components} context={{ applyFormRef }} />
        </VideoWrapper>
      </HeaderContext.Provider>
    </PageWrapper>
  );
}

CountryBenefitsPage.Layout = BenefitsLayout;
